import moment from "moment";
import "moment/locale/es";
moment.locale('es');

// Gemelo: erp_backend/Pdf/rmaCompraPdf.js (solo cambian el import y el export).
// Solicitud de autorizacion de devolucion que se le manda al proveedor.

const AZUL = '#288AC1';
const AZUL_TINTE = '#EAF6FC';
const GRIS = '#8C8C8C';
const ROJO = '#D9534F';
const BORDE = '#000000';

const ETIQUETA_ESTADO = {
  0: 'CANCELADO',
  1: 'BORRADOR',
  2: 'SOLICITADO',
  3: 'AUTORIZADO',
  4: 'RECHAZADO',
  5: 'ENVIADO',
  6: 'POR RESOLVER',
  7: 'CERRADO',
};

const bordeCompleto = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => BORDE,
  vLineColor: () => BORDE,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  paddingLeft: () => 4,
  paddingRight: () => 4,
};

const banda = (texto, color = AZUL, alignment = 'left') => ({
  text: texto,
  fillColor: color,
  color: '#FFFFFF',
  bold: true,
  fontSize: 7.5,
  alignment,
});

// Un texto sin espacios (una serie, un código pegado) es para pdfmake un solo token y se
// sale de la celda: se le siembran espacios de ancho cero (U+200B) como puntos de corte.
const cortable = (texto, cada = 20) => String(texto ?? '').replace(new RegExp(`(\\S{${cada}})`, 'g'), '$1​');

const valor = (texto, alignment = 'left') => ({
  text: texto === 0 ? '0' : (texto ? cortable(texto) : ' '),
  fontSize: 7.5,
  alignment,
});

const etiqueta = (texto) => ({ text: texto, fontSize: 7.5, bold: true, fillColor: AZUL_TINTE });

const bandaFilaCompleta = (texto, color = AZUL, alignment = 'left') => ({
  table: { widths: ['*'], body: [[banda(texto, color, alignment)]] },
  layout: bordeCompleto,
  margin: [0, 6, 0, 0],
});

// Fecha-dia anclada al mediodia UTC: se lee igual en cualquier zona de Mexico.
const fecha = (val) => (val ? moment(val).format('DD/MM/YYYY') : '');

const dinero = (n) => {
  const numero = Number(n) || 0;
  const [entero, decimales] = Math.abs(numero).toFixed(2).split('.');
  return `${numero < 0 ? '-' : ''}$${entero.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimales}`;
};

const cantidad = (n) => {
  const numero = Number(n) || 0;
  return Number.isInteger(numero) ? String(numero) : String(Math.round(numero * 10000) / 10000);
};

// Cada llamada arma sus propios widths: pdfmake los muta al calcular la tabla.
const tablaRenglones = (renglones, abreviaciones) => {
  const encabezado = [
    banda('#', AZUL, 'center'),
    banda('FACTURA', AZUL, 'center'),
    banda('CÓDIGO', AZUL, 'center'),
    banda('DESCRIPCIÓN / FALLA'),
    banda('CANTIDAD', AZUL, 'center'),
    banda('PRECIO NETO', AZUL, 'right'),
    banda('IMPORTE', AZUL, 'right'),
  ];
  const filas = renglones.map((renglon, indice) => {
    const moneda = abreviaciones?.[String(renglon.monedaId)] || '';
    const importe = (Number(renglon.cantidadSolicitada) || 0) * (Number(renglon.precioUnitarioNeto) || 0);
    return [
      valor(String(indice + 1), 'center'),
      renglon.libre
        ? valor('Sin factura', 'center')
        : {
            stack: [
              valor(`${renglon.esRemision ? 'Rem. ' : ''}${renglon.codigoFactura || ''}`, 'center'),
              { text: fecha(renglon.fechaFactura), fontSize: 6.5, color: GRIS, alignment: 'center' },
            ],
          },
      valor(renglon.codigo, 'center'),
      {
        stack: [
          valor(renglon.descripcion),
          ...(renglon.observaciones ? [{ text: cortable(renglon.observaciones), fontSize: 6.5, color: GRIS }] : []),
        ],
      },
      valor(cantidad(renglon.cantidadSolicitada), 'center'),
      valor(`${dinero(renglon.precioUnitarioNeto)} ${moneda}`.trim(), 'right'),
      valor(`${dinero(importe)} ${moneda}`.trim(), 'right'),
    ];
  });
  return {
    table: {
      headerRows: 1,
      widths: ['4%', '13%', '10%', '*', '9%', '13%', '13%'],
      body: [encabezado, ...filas],
    },
    layout: bordeCompleto,
    margin: [0, 6, 0, 0],
  };
};

// Recuadro que llena el proveedor. Si ya se capturo su autorizacion, sale impresa.
const recuadroAutorizacion = (autorizacion = {}) => ({
  table: {
    widths: ['34%', '22%', '44%'],
    body: [
      [banda('No. DE AUTORIZACIÓN DEL PROVEEDOR'), banda('FECHA'), banda('NOMBRE Y FIRMA')],
      [
        { text: autorizacion.numero || ' ', fontSize: 9, bold: true, margin: [0, 10, 0, 10] },
        { text: fecha(autorizacion.fecha) || ' ', fontSize: 8, margin: [0, 10, 0, 10] },
        { text: ' ', margin: [0, 10, 0, 10] },
      ],
    ],
  },
  layout: bordeCompleto,
  margin: [0, 6, 0, 0],
});

const rmaCompraPdf = (values) => {
  const rma = values?.rma || {};
  const empresa = values?.empresa || {};
  const configuracion = values?.configuracion || {};
  const abreviaciones = values?.abreviacionesMoneda || {};

  const renglones = rma.renglones || [];
  const tieneLogo = !!empresa?.logo?.base64Completa;
  const nombreEmpresa = (empresa?.nombreComercial || empresa?.nombre || 'EMPRESA').toUpperCase();
  const cancelado = rma.estado === 0;
  const instrucciones = (configuracion.instruccionesProveedor || '').trim();

  const dd = {
    pageSize: 'LETTER',
    pageMargins: [24, 24, 24, 30],
    ...(cancelado
      ? { watermark: { text: 'CANCELADO', color: ROJO, opacity: 0.15, bold: true } }
      : {}),
    content: [
      {
        table: {
          widths: ['24%', '52%', '24%'],
          body: [[
            tieneLogo
              ? { image: 'logoEmpresa', fit: [110, 55], alignment: 'center', margin: [0, 4, 0, 4] }
              : { text: nombreEmpresa, alignment: 'center', fontSize: 8, bold: true, margin: [0, 18, 0, 18] },
            {
              stack: [
                { text: 'SOLICITUD DE AUTORIZACIÓN', bold: true, fontSize: 12 },
                { text: 'DE DEVOLUCIÓN A PROVEEDOR', bold: true, fontSize: 12 },
                { text: '(RMA)', bold: true, fontSize: 10, color: GRIS },
              ],
              alignment: 'center',
              margin: [0, 8, 0, 6],
            },
            {
              stack: [
                { text: 'FOLIO', fontSize: 7, color: GRIS },
                { text: rma.folio || '', bold: true, fontSize: 12 },
                { text: ETIQUETA_ESTADO[rma.estado] || '', fontSize: 7.5, bold: true, color: cancelado ? ROJO : AZUL, margin: [0, 2, 0, 0] },
              ],
              alignment: 'center',
              margin: [0, 12, 0, 8],
            },
          ]],
        },
        layout: bordeCompleto,
      },
      {
        table: {
          widths: ['16%', '44%', '16%', '24%'],
          body: [
            [etiqueta('PROVEEDOR:'), valor(rma.proveedorNombre), etiqueta('FECHA:'), valor(fecha(rma.fechaSolicitud))],
            [etiqueta('MOTIVO:'), valor(rma.motivo), etiqueta('ATENCIÓN:'), valor(rma.contacto)],
            [etiqueta('CORREO:'), valor(rma.correoContacto), etiqueta('TELÉFONO:'), valor(rma.telefonoContacto)],
          ],
        },
        layout: bordeCompleto,
        margin: [0, 4, 0, 0],
      },
      renglones.length > 0
        ? tablaRenglones(renglones, abreviaciones)
        : bandaFilaCompleta('SIN RENGLONES CAPTURADOS', GRIS, 'center'),
      {
        table: {
          widths: ['*', '26%'],
          body: [[
            { text: 'TOTAL ESTIMADO CON IMPUESTOS (MXN)', fontSize: 7.5, bold: true, alignment: 'right', fillColor: AZUL_TINTE },
            { text: dinero(rma.montoSolicitado), fontSize: 8, bold: true, alignment: 'right' },
          ]],
        },
        layout: bordeCompleto,
        margin: [0, 4, 0, 0],
      },
      ...(rma.descripcion
        ? [
            bandaFilaCompleta('DESCRIPCIÓN DEL PROBLEMA'),
            { table: { widths: ['*'], body: [[valor(rma.descripcion)]] }, layout: bordeCompleto },
          ]
        : []),
      ...(instrucciones
        ? [
            bandaFilaCompleta('CONDICIONES E INSTRUCCIONES'),
            { table: { widths: ['*'], body: [[valor(instrucciones)]] }, layout: bordeCompleto },
          ]
        : []),
      recuadroAutorizacion(rma.autorizacionProveedor || {}),
      {
        text: 'Este documento no es un comprobante fiscal. Solicitamos su número de autorización (RMA) y las instrucciones para enviar la mercancía. La nota de crédito, el reemplazo o la reparación se acuerdan después de que el proveedor la revise.',
        fontSize: 6.5,
        color: GRIS,
        margin: [0, 10, 0, 0],
      },
    ],
    footer: (currentPage, pageCount) => ({
      text: `Página ${currentPage} de ${pageCount}`,
      alignment: 'right',
      fontSize: 7,
      color: '#888',
      margin: [0, 0, 24, 0],
    }),
    images: tieneLogo ? { logoEmpresa: empresa.logo.base64Completa } : {},
    defaultStyle: { fontSize: 7.5 },
  };

  return dd;
};

export default rmaCompraPdf;
